<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractLifecycle',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const currencies = ref(null);
  const billing_frequency = ref(null);
  const indexingRules = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    currencies: currencies.value,
    billing_frequency: billing_frequency.value,
    indexingRules: indexingRules.value,
    ux: ux.value,
  }));

  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_billing_frequency = async () => {
    return props.contract.billing_cycle != null
      ? {
          value: props.contract.billing_cycle.split(' ')[0],
          unit: props.contract.billing_cycle.split(' ')[1],
        }
      : {
          value: null,
          unit: null,
        };
  };
  const $$load_indexingRules = async () => {
    return $fn.value.getEnumValuesFromSchema('IndexingRule');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'currencies' },
      { name: 'billing_frequency' },
      { name: 'indexingRules' },
      { name: 'ux' },
    ],
    loaders: {
      currencies: $$load_currencies,
      billing_frequency: $$load_billing_frequency,
      indexingRules: $$load_indexingRules,
    },
    ux: {},
    dataRefs: { currencies, billing_frequency, indexingRules, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_d3nvz4yhq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_eomb962fa =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_d3nvz4yhq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_eomb962fa],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_eomb962fa,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_eomb962fa = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_eomb962fa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="revision_date"
      :label="pgettext('Contract', 'Contract date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      v-if="contract.revision_number == 0"
    >
      <ItpFormDatetime
        name="revision_date"
        v-model="contract.lifecycle.revision_date"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="revision_date"
      :label="pgettext('Contract', 'Revision date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      v-if="contract.revision_number > 0"
    >
      <ItpFormDatetime
        name="revision_date"
        v-model="contract.lifecycle.revision_date"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="valid_from"
      :label="pgettext('Contract', 'Validity')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      contentClass="row"
    >
      <div class="col">
        <ItpFormDatetime
          name="valid_from"
          v-model="contract.lifecycle.valid_from"
        >
        </ItpFormDatetime>
      </div>
      <div class="col">
        <ItpFormDatetime name="valid_to" v-model="contract.lifecycle.valid_to">
        </ItpFormDatetime>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      name="date_signed"
      :label="pgettext('Contract', 'Date signed')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="date_signed"
        v-model="contract.lifecycle.date_signed"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="effective_date"
      :label="pgettext('Contract', 'Effective date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="effective_date"
        v-model="contract.lifecycle.effective_date"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="date_of_occupancy"
      :label="pgettext('Contract', 'Date of occupancy')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="date_of_occupancy"
        v-model="contract.lifecycle.date_of_occupancy"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="auto_renewal"
      :label="pgettext('Contract', 'Auto renewal')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox
        name="auto_renewal"
        v-model="contract.lifecycle.auto_renewal"
        :value="contract.lifecycle.auto_renewal"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.lifecycle.auto_renewal =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormCheckbox>
    </ItpFormGroup>
    <ItpFormGroup
      name="notice_period"
      :label="pgettext('Contract', 'Notice period')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <ItpFormInput
        type="number"
        name="notice_period"
        v-model="contract.lifecycle.notice_period"
        :value="contract.lifecycle.notice_period"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.lifecycle.notice_period =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
      <span class="input-group-text">
        {{ pgettext('InputGroupAddon', 'days') }}
      </span>
    </ItpFormGroup>
    <ItpFormGroup
      name="indexing_rule"
      :label="pgettext('Contract', 'Indexing rule')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormSelect2
        name="indexing_rule"
        v-model="contract.lifecycle.indexing_rule"
        :value="contract.lifecycle.indexing_rule"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.lifecycle.indexing_rule =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="indexingRules"
        valueField="value"
        textField="text"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="next_indexation_date"
      :label="pgettext('Contract', 'Next indexation date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="next_indexation_date"
        v-model="contract.lifecycle.next_indexation_date"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="termination_date"
      :label="pgettext('Contract', 'Terminaton date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="termination_date"
        v-model="contract.lifecycle.termination_date"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="termination_effective_date"
      :label="pgettext('Contract', 'Terminaton effective date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="termination_effective_date"
        v-model="contract.lifecycle.termination_effective_date"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
